// src/api/client.ts
import axios, {
  AxiosError,
  AxiosHeaders,
  type InternalAxiosRequestConfig,
} from "axios";
import { getToken } from "../lib/auth";
import { API_BASE_URL, getApiBaseForMode } from "../lib/apiBase";
import { handleAuthFailure } from "../lib/sessionRecovery";
import {
  emitCloudFence,
  emitCloudNetworkError,
  emitCloudSlow,
  getEdgeMode,
  isEdgeLocalMode,
  markCloudResponded,
  SLOW_AFTER_MS,
  type EdgeMode,
} from "../lib/edge/edgeState";
import { budgetFor, isReadMethod } from "./timeouts";
import {
  IDEMPOTENCY_KEY_FIELD,
  fingerprintOf,
  forgetKey,
  isIdempotentMutation,
  isPlainBody,
  keyFor,
} from "./idempotency";
import { getEdgeTicketForToken } from "../lib/edge/edgeTicket";
import {
  mirrorCloudResponse,
  shouldMirrorPath,
  splitRequestUrl,
} from "../lib/edge/edgeMirror";
import type { RootState } from "../store/store";
import i18n from "../translations/i18n";

// Заявки с `skipAuthRedirect: true` сами си обработват 401 (напр. възстановяване
// на служителска сесия след refresh) — интерсепторът не чисти токена и не
// редиректва към /login.
// `authToken` се закача от request интерсептора: response интерсепторът трябва
// да знае С КОЙ токен е тръгнала провалилата се заявка, за да не изтрие живия
// токен на нов служител (виж lib/sessionRecovery.ts). Четенето от
// `config.headers.Authorization` не е надеждно — axios ремапва регистъра при
// merge с defaults.
// `edgeLocal` също се закача от request интерсептора: заявката е тръгнала към
// КАСАТА (локален режим, docs/edge.md), не към облака — 401 оттам не пипа
// сесията, а отговорът ѝ не се огледва.
// `slowClockId` е номерът на часовника „бавно" за тази заявка (виж по-долу).
// `modeEpoch` е епохата на режима, с която заявката е ТРЪГНАЛА, а
// `edgeRetried` бележи единственото ѝ повторение срещу касата (виж
// §Повторение след смяна на режима). `idempotencyFingerprint` е отпечатъкът на
// тялото, под който е помнен ключът за идемпотентност (`api/idempotency.ts`).
declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
    authToken?: string | null;
    edgeLocal?: boolean;
    slowClockId?: number;
    modeEpoch?: number;
    edgeRetried?: boolean;
    idempotencyFingerprint?: string;
  }
}

/** Хедър на отговора — и от `AxiosHeaders`, и от обикновен обект (мокове). */
function responseHeader(
  response: { headers?: unknown } | undefined,
  name: string,
): string | null {
  const headers = response?.headers as
    | { [key: string]: unknown; get?: (name: string) => unknown }
    | undefined;
  const raw =
    typeof headers?.get === "function"
      ? headers.get(name)
      : (headers?.[name] ?? headers?.[name.toLowerCase()]);
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

export const api = axios.create({ baseURL: API_BASE_URL });

// ─── Часовник „бавно" ──────────────────────────────────────────────────────
// Заявка, която мълчи повече от 1,5 s, е първият видим знак, че нещо не е
// наред — по-рано от всеки таймаут. Тя пуска сондите и лентата, вместо
// сервитьорът да гледа въртележка и да натиска втори път.
//
// Часовниците живеят в Map, а не само на config-а: има пътища, по които
// отговорът никога не стига до интерсептора (грешка в друг интерсептор,
// прекъснат adapter). Затова всеки часовник се маха и САМ, когато удари —
// таблицата държи само летящите и не расте.
const slowClocks = new Map<number, ReturnType<typeof setTimeout>>();
let slowClockSerial = 0;

function armSlowClock(config: InternalAxiosRequestConfig) {
  const id = ++slowClockSerial;
  config.slowClockId = id;
  slowClocks.set(
    id,
    setTimeout(() => {
      slowClocks.delete(id);
      emitCloudSlow();
    }, SLOW_AFTER_MS),
  );
}

function disarmSlowClock(config?: { slowClockId?: number }) {
  const id = config?.slowClockId;
  if (id === undefined) return;
  const timer = slowClocks.get(id);
  if (timer === undefined) return;
  clearTimeout(timer);
  slowClocks.delete(id);
}

// ─── Отговор ОТ ОБЛАКА ли е това изобщо ────────────────────────────────────
// Прихващащ прокси (хотелски портал, ISP редирект „неплатена сметка", ingress
// без backend) отговаря 200 или 404 с HTML на СВОЯ вход. На ниво HTTP 200 си е
// 200 — но нашето BE НИКОГА не отговаря с HTML: всеки негов отговор, включително
// всяка грешка, е JSON. Затова за „облакът отговори" се брои само JSON тяло.
//
// Без тази проверка всеки такъв фалшив успех сваляше брояча на сондите на нула
// (`markCloudResponded` → `noteCloudAlive`) и обектът не можеше да замръзне,
// докато сервитьорът работи — тоест точно в аварията, в която всяка поръчка
// отива в HTML страницата на портала.
//
// Файловете (снимки, износ) не носят JSON и затова не се броят за
// свидетелство. Това не пречи на нищо: решението е на сондата `GET /health`,
// която чете тялото и знае разликата.
function isCloudOwnResponse(response?: {
  headers?: unknown;
  data?: unknown;
}): boolean {
  if (!response) return false;
  const headers = response.headers as
    | { [key: string]: unknown; get?: (name: string) => unknown }
    | undefined;
  const raw =
    typeof headers?.get === "function"
      ? headers.get("content-type")
      : (headers?.["content-type"] ?? headers?.["Content-Type"]);
  if (typeof raw === "string" && raw.length > 0) {
    return raw.toLowerCase().includes("json");
  }
  // Без content-type (стар мок, адаптер, който не го подава) се съди по
  // тялото: обект или масив значи, че axios е разчел JSON.
  return typeof response.data === "object" && response.data !== null;
}

// ─── Епоха на режима ───────────────────────────────────────────────────────
// Номер, който расте при ВСЯКА смяна на режима (облак ⇄ каса). Всяка заявка
// заминава с епохата, която е заварила; при провал се сравнява с текущата и
// така се вижда дали режимът се е сменил ПОД нея.
//
// Режимът се чете от `edgeState` (единственият му писач е `edgeMode.ts`) —
// тук няма абонамент нарочно: смяната се ЗАСИЧА при четене, а четене има и на
// тръгване, и на провал, тоест нито една смяна не остава невидяна за заявка,
// която е била във въздуха.
let lastSeenMode: EdgeMode = getEdgeMode();
let modeEpoch = 0;

function modeEpochNow(): number {
  const mode = getEdgeMode();
  if (mode !== lastSeenMode) {
    lastSeenMode = mode;
    modeEpoch += 1;
  }
  return modeEpoch;
}

// ─── Повторение след смяна на режима ───────────────────────────────────────
// ДЕФЕКТЪТ: първото действие след спиране на интернета не прави НИЩО и
// терминалът трябва да се презареди. Заявката тръгва към ОБЛАКА, умира там, а
// превключването (сондите + `mode/enter`) става ДОКАТО тя виси — тоест
// лентата вече пише „локален режим", а операцията е вече пропаднала срещу
// адрес, който го няма. Никой не я повтаря срещу касата.
//
// Условията са нарочно тесни — всяко от тях пази нещо:
//  • само МРЕЖОВА грешка (без `response`): отговорил сървър, 4xx или 5xx, е
//    решение по същество и повторението му е втора заявка за същото;
//  • само ако режимът се е СМЕНИЛ между тръгването и провала (епохата) И
//    сегашният режим е локален: заявка, тръгнала към касата, не се препраща
//    към облака, а флап облак→каса→облак не се повтаря никъде;
//  • точно ЕДИН опит (`edgeRetried`) — никога цикъл. Провали ли се и той,
//    грешката отива при извикващия, както досега;
//  • само ЧЕТЕНЕ. Виж по-долу защо мутациите остават навън.
//
// ЗАЩО НЕ И МУТАЦИИТЕ. Доводът „мрежовата грешка гарантира, че заявката не е
// стигнала до облака" НЕ Е верен: `ECONNABORTED` (изтекъл бюджет) значи, че
// заявката Е изпратена и отговорът просто не се е върнал, а и `ERR_NETWORK`
// покрива скъсана връзка СЛЕД като сървърът е приел тялото. Тоест повторено
// закриване срещу касата може да е ВТОРИ бон за същите пари, а повторено
// `POST /bills` — втора поръчка в кухнята (точно „мазалото", което се
// докладва). Затова тук важи същото правило като на react-query
// (`main.tsx`: `mutations: { retry: 0 }`) — мутация не се повтаря сама
// НИКОГА. Операторът натиска пак, вече срещу касата, и вижда какво става.
function switchedToTillMidFlight(err: AxiosError): boolean {
  const config = err.config;
  if (!config) return false;
  // Заявката вече е била към касата (или е повторена веднъж) — край.
  if (config.edgeLocal || config.edgeRetried) return false;
  // Отговор от сървър = решение по същество. Отменената заявка не е провал.
  if (err.response) return false;
  if (err.code === "ERR_CANCELED") return false;
  if (modeEpochNow() === config.modeEpoch) return false;
  return isEdgeLocalMode();
}

/**
 * Провалила се е, без облакът да е казал нищо — таймаут, DNS, отказана
 * връзка, отменен превключвател. Екраните го ползват, за да покажат „няма
 * връзка", вместо общото „Грешка": при мрежова грешка `detail` от BE-то няма
 * и всеки `getApiErrorMessage` пада на резервния текст.
 */
export function isCloudUnreachableError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return false;
  return error.code !== "ERR_CANCELED";
}

// Глобална референция към store за достъп до Redux state
// Това дава на axios interceptor-а достъп до Redux store-а, за да може да чете текущия токен
let storeRef: { getState: () => RootState } | null = null;

export function setStoreRef(store: { getState: () => RootState }) {
  storeRef = store;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Вземи Redux токена ако има store референция
  const reduxToken = storeRef?.getState().auth.token ?? null;
  // Подавам и jwt Token-a от Redux, ако сме от Общ акаунт да използваме него, а не този от LocalStorage-a
  const token = getToken(reduxToken);
  const headers = AxiosHeaders.from(config.headers);
  // Режимът се чете ПРИ ВСЯКА заявка (docs/edge.md): в локален режим адресът
  // е главната каса (същите пътища) и заявката носи билета (`X-Edge-Ticket`).
  const edgeLocal = isEdgeLocalMode();
  // Облачният JWT НЕ отива към касата (одит 01.10.2026, F5): адресът ѝ е
  // `http://` в локалната мрежа и всеки в нея би го прочел — а с него се влиза в
  // ОБЛАКА до изтичането му. Касата чете само ограничения си билет.
  if (token && !edgeLocal) headers.set("Authorization", `Bearer ${token}`);
  else headers.delete("Authorization");
  config.baseURL = getApiBaseForMode();
  config.edgeLocal = edgeLocal;
  // Епохата, с която заявката тръгва — при провал по нея се вижда дали
  // режимът се е сменил под нея (§Повторение след смяна на режима).
  config.modeEpoch = modeEpochNow();
  const ticket = edgeLocal ? getEdgeTicketForToken(token) : null;
  if (ticket) headers.set("X-Edge-Ticket", ticket);
  config.headers = headers;
  // ОГРАНИЧЕН локален режим (§14.3.1 на договора, docs/edge.md): срещу касата
  // сме, но за активния токен няма билет (терминалът е бил изгасен и билетът
  // с TTL 24 h е изтекъл). Касата приема само `POST {edge}/user/login` и
  // `/user/login-with-code` — а те вървят със суров axios в `api/auth.ts` и
  // не минават оттук. Всичко друго се отказва БЕЗ заявка: иначе е гарантен
  // 401 от касата и нов prompt за локалната мрежа. Формата е на мрежова
  // грешка (без `response`), за да не мине за отказ на сървъра — екраните
  // показват вход, а самоличността от кеша остава.
  if (edgeLocal && !ticket) {
    throw new AxiosError(
      i18n.t("edge.restrictedRequest"),
      "ERR_EDGE_RESTRICTED",
      config,
    );
  }
  // Запазваме токена на самата заявка — response интерсепторът го ползва, за да
  // разбере ЧИЙ токен е гръмнал (compare-and-clear в sessionRecovery).
  config.authToken = token;
  // Ключ за идемпотентност (одит 01.10.2026, N-01): поръчка/сторно/внос,
  // повторени със същото тяло СЛЕД провал с неизвестен изход, носят същия
  // ключ — касата и облакът не ги изпълняват втори път.
  if (isIdempotentMutation(config.method, config.url) && isPlainBody(config.data)) {
    const fingerprint = fingerprintOf(config.url, config.data);
    config.idempotencyFingerprint = fingerprint;
    config.data = { ...config.data, [IDEMPOTENCY_KEY_FIELD]: keyFor(fingerprint) };
  }
  // Бюджет по МАРШРУТ (`api/timeouts.ts`). Без него заявката при спрян
  // интернет виси до таймаута на ОС-а (35–90 s); с ОБЩ бюджет пък закриване с
  // карта би се отказвало, докато гостът въвежда ПИН на терминала. Изрично
  // подаденият таймаут (сондите) печели — те си знаят числото.
  if (!config.timeout) {
    config.timeout = budgetFor(config.method ?? "get", config.url ?? "", config.data);
  }
  // Часовникът върви само за облака: касата е в същата стая и нейното
  // мълчание значи друго (§сондите към касата).
  if (!edgeLocal) armSlowClock(config);
  return config;
});

api.interceptors.response.use(
  (res) => {
    disarmSlowClock(res.config);
    // Категоричен изход — следващата еднаква поръчка е нова (N-01).
    forgetKey(res.config.idempotencyFingerprint);
    // Облакът отговори в това зареждане — оттук нататък загубата му минава
    // през хистерезиса на сондите, не през „веднага в локален режим"
    // (lib/edge/edgeMode.ts §Студен старт).
    if (!res.config.edgeLocal) markCloudResponded(isCloudOwnResponse(res));
    // Огледало към касата (§4 на договора): всеки успешен GET към ОБЛАКА по
    // пътищата, които касата сервира без интернет. Fire-and-forget — нищо
    // тук не бави и не проваля отговора.
    // ...но само ако отговорът е НАШ: HTML-ът на прихващащ прокси, изпратен в
    // огледалото, става менюто, с което касата ще работи без интернет.
    // `/bills` тя отхвърля сама (не е списък), останалите пътища — не.
    if (
      res.config.method === "get" &&
      !res.config.edgeLocal &&
      res.status >= 200 &&
      res.status < 300 &&
      isCloudOwnResponse(res)
    ) {
      const { path, query } = splitRequestUrl(
        res.config.url,
        res.config.params,
      );
      if (shouldMirrorPath(path)) {
        mirrorCloudResponse({
          path,
          query,
          body: res.data,
          ticket: getEdgeTicketForToken(res.config.authToken),
          // Поколението на изгледа (одит 01.10.2026, F4): касата приема само
          // по-нов изглед от последния приет, не последния ПРИСТИГНАЛ.
          viewAt: responseHeader(res, "x-edge-view-at"),
        });
      }
    }
    return res;
  },
  (err) => {
    disarmSlowClock(err.config);
    // Отговорил сървър под 500 е КАТЕГОРИЧЕН отказ — ключът се забравя. Мрежова
    // грешка, таймаут и 5xx са неизвестен изход: ключът остава за повторението.
    if (
      axios.isAxiosError(err) &&
      err.response?.status !== undefined &&
      err.response.status < 500
    ) {
      forgetKey(err.config?.idempotencyFingerprint);
    }
    // Мрежова грешка (без HTTP отговор) от облака — подсказка за
    // lib/edge/edgeMode.ts да пусне сондите; отменена заявка не е мрежов проблем.
    // 4xx значи, че облакът е достижим и работи (отказва по същество) — брои
    // за „отговорил в това зареждане".
    // 5xx НЕ значи жив облак (§16.4 №6 на договора, „половин интернет"):
    // обратният proxy отговаря, а BE-то или базата му са долу. Дотук такъв
    // отговор минаваше за достижим облак, сондите не тръгваха и терминалът
    // оставаше залостен срещу мъртъв облак при работеща каса. Сега 5xx е
    // същата подсказка като мрежовата грешка — а РЕШАВА пак `GET {cloud}/health`
    // (3 поредни провала), тоест единичен 500 от счупена справка не сваля
    // терминала в локален режим.
    if (axios.isAxiosError(err) && !err.config?.edgeLocal) {
      const status = err.response?.status;
      const cloudBroken = status !== undefined && status >= 500;
      if (status !== undefined && !cloudBroken) {
        // 4xx от НАШЕТО BE е JSON с `detail`; 404 с HTML от ingress без
        // backend — не е. Второто не е свидетелство, че облакът е жив.
        markCloudResponded(isCloudOwnResponse(err.response));
      } else if (err.code !== "ERR_CANCELED") {
        emitCloudNetworkError(cloudBroken ? "app_down" : "down");
      }
      // 423 НЕ Е ГРЕШКА (§fence): облакът сам е решил, че обектът е в локален
      // режим — касата вече работи офлайн и той отказва да издаде втори бон.
      // Дотук сервитьорът виждаше „Грешка" и нямаше какво да направи; сега
      // терминалът превключва срещу касата и повтарянето е възможно.
      if (status === 423) emitCloudFence();
    }
    // ЕДИН повторен опит срещу касата, когато режимът се е сменил ПОД
    // заявката (§Повторение след смяна на режима). Стои СЛЕД сигналите горе
    // нарочно: провалът трябва да стигне до сондите дори когато повторението
    // успее — иначе хистерезисът брои с един провал по-малко.
    //
    // Адресът, билетът и бюджетът се закачат наново от request интерсептора,
    // защото повторението минава през него. Той е и мястото, което отказва
    // заявка БЕЗ билет за касата (ОГРАНИЧЕН локален режим): тогава до
    // мрежата не се стига и извикващият получава ясното
    // „Локален режим — влез с код или парола" вместо мълчание. Това е един
    // и същ отказ на едно и също място — тук не се преповтаря.
    if (axios.isAxiosError(err) && err.config && switchedToTillMidFlight(err)) {
      const config: InternalAxiosRequestConfig = err.config;
      if (isReadMethod(config.method ?? "get")) {
        config.edgeRetried = true;
        return api.request(config);
      }
    }
    // 401 от КАСАТА (невалиден/изтекъл билет) НЕ пипа сесията: пробата на
    // терминалния токен е срещу облака, който точно сега го няма, а изход
    // без интернет = мъртъв терминал. Грешката стига до извикващия като detail.
    if (
      err.response?.status === 401 &&
      !err.config?.skipAuthRedirect &&
      !err.config?.edgeLocal
    ) {
      // НЕ чистим токена тук. На общ терминал 401 почти винаги значи само
      // подменена СЛУЖИТЕЛСКА сесия, а трите ключа в localStorage са общи за
      // всички табове/прозорци — триенето им сваля и валидния съсед.
      // Целият избор „до ПИН или пълен изход" живее в sessionRecovery.
      void handleAuthFailure({
        failedToken: err.config?.authToken,
        source: "http-401",
      });
    }
    // Локализираме BE-то "Missing permission: <name>" → преведено съобщение,
    // така че всички съществуващи catch блокове, които четат
    // response.data.detail, автоматично получават локализирания текст.
    if (err.response?.status === 403) {
      const detail: unknown = err.response?.data?.detail;
      if (typeof detail === "string") {
        const match = detail.match(/^Missing permission:\s*(\S+)$/);
        if (match) {
          err.response.data.detail = i18n.t("errors.missingPermission", {
            permission: match[1],
          });
        }
      }
    }
    return Promise.reject(err);
  },
);
